import { CheckOutlined } from '@ant-design/icons';
import { Button, message, Modal, Space, Typography } from 'antd';
import { FC, useEffect, useState } from 'react';
import { AuthResult, AzureBlobResponse } from '../../../../../shared/file-upload/azure-upload';
import { FileInfo } from '../../../../../../generated';
import FilePreview from '../../../../../shared/file-preview/file-preview';
import { FileUploadButton } from '../../../../../shared/file-upload/file-upload-button';
import { VerificationStatusReportDocumentPickerContainer } from './verification-status-report-document-picker.container';
import { CoverLetterDocumentPickerContainer } from './cover-letter-document-picker.container';

interface FileUploadWithGenerateVerificationStatusReportButtonProps {
  title?: string;
  caseId: string;
  description?: string; // text below the title (optional)
  buttonText?: string; // text on the upload button
  permittedContentTypes?: string[];
  permittedExtensions?: string[];
  onAuthorizeRequest: (file: File) => Promise<AuthResult>; // called before uploading or when previewing a file
  fetchFilePreviewData: () => Promise<FileInfo>;
  onUploadSuccess: (uploadedData: AzureBlobResponse) => Promise<void>;
  isPreviewOnly?: boolean;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
  isShowFileName?: boolean;
  isUploadingPreviewImage?: boolean;
  isCoverLetter: boolean;
  verificationFileNames: Map<string, string>;
  verificationFileName: string;
}

export const FileUploadWithGenerateVerificationStatusReportButton: FC<FileUploadWithGenerateVerificationStatusReportButtonProps> = ({
  isPreviewOnly = false,
  ...props
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [previewFileData, setPreviewFileData] = useState<FileInfo | null>(null);
  const [isProcessingUploadingFile, setIsProcessingUploadingFile] = useState(false);
  const [isProcessingGeneratingPdf, setIsProcessingGeneratingPdf] = useState(false);
  const [hasNewFileUploaded, setHasNewFileUploaded] = useState(false);

  const handleMergePdfComplete = async () => {
    setIsModalVisible(false);
    await getFilePreviewData();
    if (props.isCoverLetter) {
      message.success('Cover Letter generated successfully');
      props.setIsEditing && props.setIsEditing(false);
    } else {
      message.success('Verification Status Report generated successfully');
      props.setIsEditing && props.setIsEditing(false);
    }
    setHasNewFileUploaded(true);
  };

  useEffect(() => {
    const doGetFilePreviewData = async () => {
      setIsProcessingUploadingFile(true);
      await getFilePreviewData();
      setIsProcessingUploadingFile(false);
    };
    doGetFilePreviewData();
  }, []);

  const getFilePreviewData = async () => {
    const fileData = await props.fetchFilePreviewData();
    setPreviewFileData(fileData);
  };

  const handleAuthorizeRequestForUploadFile = async (file: File): Promise<AuthResult> => {
    setIsProcessingUploadingFile(true);
    setHasNewFileUploaded(false);
    return props.onAuthorizeRequest(file);
  };

  // const handleAuthorizeRequestForUploadGeneratedPdf = async (file: File): Promise<AuthResult> => {
  //   setHasNewFileUploaded(false);
  //   return props.onAuthorizeRequest(file);
  // };

  const handleOnError = () => {
    setIsProcessingUploadingFile(false);
    setIsProcessingGeneratingPdf(false);
  };

  const handleOnUploadSuccess = async (response: AzureBlobResponse) => {
    await doLastStepAfterUploadOrGeneratePdfSuccess(response);
    message.success('File uploaded successfully');
    props.setIsEditing && props.setIsEditing(false);
  };


  const doLastStepAfterUploadOrGeneratePdfSuccess = async (response: AzureBlobResponse) => {
    await getFilePreviewData();
    if (props.setIsEditing) {
      props.setIsEditing(!isPreviewOnly);
    }
    await props.onUploadSuccess(response);
    setHasNewFileUploaded(true);
    setIsProcessingUploadingFile(false);
    setIsProcessingGeneratingPdf(false);
    setIsModalVisible(false);
  };


  const handleStartPdfGeneration = () => {
    setHasNewFileUploaded(false);
    setIsProcessingGeneratingPdf(true);
  };


  const handleOnGeneratePdfSuccess = async (response: AzureBlobResponse) => {
    await doLastStepAfterUploadOrGeneratePdfSuccess(response);
  };

  const handleAuthorizeRequestForUploadGeneratedPdf = async (file: File): Promise<AuthResult> => {
    setHasNewFileUploaded(false);
    return props.onAuthorizeRequest(file);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };
  return (
    <div
      style={{
        fontSize: '10px',
        margin: '10px'
      }}
    >
      {props.title && <Typography.Title level={5}>{props.title}</Typography.Title>}
      {props.description && (
        <div
          style={{
            marginBottom: '10px'
          }}
        >
          {props.description}
        </div>
      )}
      <Space direction="horizontal" style={{ alignItems: 'baseline' }}>
        <FilePreview
          fileUrl={previewFileData?.url ?? ''}
          fileName={previewFileData?.name ?? ''}
          fileType={previewFileData?.type ?? ''}
          isProcessing={isProcessingUploadingFile || isProcessingGeneratingPdf}
          isShowFileName={props.isShowFileName}
        />
        {hasNewFileUploaded && <CheckOutlined style={{ color: '#52c41a', paddingLeft: '5px' }} />}
      </Space>
      <br></br>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {!isPreviewOnly && (
          <Button
            type="primary"
            style={{
              marginTop: '10px',
              width: '160px'            }}
            disabled={isProcessingUploadingFile}
            onClick={() => setIsModalVisible(true)}
          >
            Generate PDF
          </Button>
        )}
        <Modal
          destroyOnClose={true}
          onCancel={() => setIsModalVisible(false)}
          open={isModalVisible}
          onOk={() => setIsModalVisible(false)}
          footer={null}
          closable={false}
          style={{
            width: '100%',
            minWidth: 'fit-content'
          }}
        >
          {props.isCoverLetter ? (
            <CoverLetterDocumentPickerContainer
              onCancel={handleCancel}
              caseId={props.caseId ?? ''}
              onStartPdfGeneration={handleStartPdfGeneration}
              isProcessing={isProcessingGeneratingPdf}
              authorizeRequest={handleAuthorizeRequestForUploadGeneratedPdf}
              onSuccess={handleOnGeneratePdfSuccess}
              isProcessingUploadingFile={isProcessingUploadingFile}
              setIsEditing={props.setIsEditing}

            />
          ) : (
            <VerificationStatusReportDocumentPickerContainer
              onCancel={handleCancel}
              onMergePdfComplete={handleMergePdfComplete}
              verificationFileNames={props.verificationFileNames}
              verificationFileName={props.verificationFileName}
              caseId={props.caseId ?? ''}
            />
          )}
        </Modal>
        {!isPreviewOnly && (
          <FileUploadButton
            isUploadPreviewImage={props.isUploadingPreviewImage}
            isProcessing={isProcessingUploadingFile}
            isProcessingGeneratingPdf={isProcessingGeneratingPdf}
            authorizeRequest={handleAuthorizeRequestForUploadFile}
            buttonText={props.buttonText}
            maxFileCount={1}
            permittedContentTypes={props.permittedContentTypes}
            permittedExtensions={props.permittedExtensions}
            maxFileSizeBytes={10 * 1024 * 1024} // 10MB,
            maxWidthOrHeight={2048}
            onSuccess={handleOnUploadSuccess}
            onError={handleOnError}
          />
        )}
      </div>
    </div>
  );
};
